/* ==========================================================================
   Zooted Prestige — global.js
   Header behavior, navigation, reveals, parallax, sliders, accordions,
   page transitions. Vanilla JS only — no frameworks.
   ========================================================================== */
(function () {
  'use strict';

  var settings = window.zootedSettings || {};
  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var motionOff = prefersReduced || settings.animation === 'off';
  if (prefersReduced) document.body.classList.add('reduced-motion');

  /* ------------------------------------------------------------------
     Utilities
     ------------------------------------------------------------------ */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function trapFocus(container, e) {
    var focusables = $$('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])', container)
      .filter(function (el) { return el.offsetParent !== null; });
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
  window.zootedTrapFocus = trapFocus;

  /* ------------------------------------------------------------------
     Scroll reveal
     ------------------------------------------------------------------ */
  function initReveals() {
    var nodes = $$('[data-reveal]');
    if (!nodes.length) return;
    if (motionOff || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.classList.add('is-revealed'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    nodes.forEach(function (n) { io.observe(n); });
  }

  /* ------------------------------------------------------------------
     Parallax media
     ------------------------------------------------------------------ */
  function initParallax() {
    var nodes = $$('.parallax-media');
    if (!nodes.length || motionOff) return;
    var ticking = false;
    function update() {
      ticking = false;
      var vh = window.innerHeight;
      nodes.forEach(function (node) {
        var rect = node.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > vh) return;
        var progress = (rect.top + rect.height / 2 - vh / 2) / vh; // -0.5 .. 0.5
        var shift = Math.max(-1, Math.min(1, progress)) * -6; // percent
        node.style.setProperty('--parallax-y', shift + '%');
      });
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  /* ------------------------------------------------------------------
     Header: transparent-over-hero, scroll state, hide on scroll down
     ------------------------------------------------------------------ */
  function initHeader() {
    var header = $('.site-header');
    if (!header) return;

    var isTransparent = header.classList.contains('site-header--transparent');
    var lastY = window.scrollY;
    var bar = $('.announcement-bar');
    var barHeight = bar ? bar.offsetHeight : 0;

    function onScroll() {
      var y = window.scrollY;
      if (barHeight) {
        document.documentElement.style.setProperty('--announcement-offset', Math.max(0, barHeight - y) + 'px');
      }
      header.classList.toggle('is-scrolled', y > 24);
      // Header stays visible at all times — no hide-on-scroll.
      lastY = y;
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    // If transparent mode requested but page starts without a hero, fall back to solid.
    if (isTransparent) {
      var main = $('#MainContent');
      var first = main && main.querySelector('.shopify-section:first-child');
      var hasHero = first && first.querySelector('.hero, .collection-hero, .cannabis-hero, .story');
      if (!hasHero) header.classList.remove('site-header--transparent');
    }

    /* Mega menu */
    $$('[data-mega-trigger]').forEach(function (trigger) {
      var menu = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!menu) return;
      var closeTimer;
      function open() {
        clearTimeout(closeTimer);
        menu.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
      }
      function close() {
        menu.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
      }
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        menu.classList.contains('is-open') ? close() : open();
      });
      trigger.addEventListener('mouseenter', open);
      trigger.addEventListener('mouseleave', function () { closeTimer = setTimeout(close, 180); });
      menu.addEventListener('mouseenter', function () { clearTimeout(closeTimer); });
      menu.addEventListener('mouseleave', function () { closeTimer = setTimeout(close, 180); });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
      document.addEventListener('click', function (e) {
        if (!menu.contains(e.target) && !trigger.contains(e.target)) close();
      });
    });

    /* Mobile menu */
    var burger = $('[data-menu-toggle]');
    var mobileMenu = $('#MobileMenu');
    if (burger && mobileMenu) {
      function setMenu(openState) {
        mobileMenu.classList.toggle('is-open', openState);
        burger.setAttribute('aria-expanded', openState ? 'true' : 'false');
        header.classList.toggle('is-open', openState);
        document.body.classList.toggle('no-scroll', openState);
        if (openState) {
          var firstLink = mobileMenu.querySelector('a, button, summary');
          if (firstLink) firstLink.focus();
        }
      }
      burger.addEventListener('click', function () {
        setMenu(!mobileMenu.classList.contains('is-open'));
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && mobileMenu.classList.contains('is-open')) { setMenu(false); burger.focus(); }
        if (e.key === 'Tab' && mobileMenu.classList.contains('is-open')) trapFocus(mobileMenu, e);
      });
    }

    /* Search overlay */
    var searchToggle = $('[data-search-toggle]');
    var searchOverlay = $('#SearchOverlay');
    if (searchToggle && searchOverlay) {
      function setSearch(openState) {
        searchOverlay.classList.toggle('is-open', openState);
        searchToggle.setAttribute('aria-expanded', openState ? 'true' : 'false');
        document.body.classList.toggle('no-scroll', openState);
        if (openState) {
          var input = searchOverlay.querySelector('input[type="search"]');
          if (input) setTimeout(function () { input.focus(); }, 60);
        }
      }
      searchToggle.addEventListener('click', function () { setSearch(true); });
      searchOverlay.addEventListener('click', function (e) {
        if (e.target === searchOverlay || e.target.closest('[data-search-close]')) setSearch(false);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && searchOverlay.classList.contains('is-open')) { setSearch(false); searchToggle.focus(); }
        if (e.key === 'Tab' && searchOverlay.classList.contains('is-open')) trapFocus(searchOverlay, e);
      });
    }
  }

  /* ------------------------------------------------------------------
     Hero staged text reveal
     ------------------------------------------------------------------ */
  function initHeroes() {
    $$('.hero').forEach(function (hero) {
      $$('[data-hero-stagger]', hero).forEach(function (el, i) { el.style.setProperty('--i', i); });
      requestAnimationFrame(function () { hero.classList.add('is-loaded'); });
    });
  }

  /* ------------------------------------------------------------------
     Product model slider (tabs + arrows + swipe + keyboard)
     ------------------------------------------------------------------ */
  function initModelSliders() {
    $$('[data-model-slider]').forEach(function (slider) {
      var tabs = $$('.model-tab', slider);
      var slides = $$('.model-slide', slider);
      if (!slides.length) return;
      var index = 0; var booted = false;

      function go(i, focusTab) {
        index = (i + slides.length) % slides.length;
        slides.forEach(function (s, k) {
          s.classList.toggle('is-active', k === index);
          s.toggleAttribute('hidden', k !== index);
        });
        tabs.forEach(function (t, k) {
          t.setAttribute('aria-selected', k === index ? 'true' : 'false');
          t.setAttribute('tabindex', k === index ? '0' : '-1');
          if (k === index) {
            if (booted) t.scrollIntoView({ block: 'nearest', inline: 'center', behavior: motionOff ? 'auto' : 'smooth' });
            if (focusTab) t.focus();
          }
        });
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { go(i); });
        tab.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowRight') { e.preventDefault(); go(index + 1, true); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); go(index - 1, true); }
          if (e.key === 'Home') { e.preventDefault(); go(0, true); }
          if (e.key === 'End') { e.preventDefault(); go(slides.length - 1, true); }
        });
      });
      $$('[data-model-prev]', slider).forEach(function (b) { b.addEventListener('click', function () { go(index - 1); }); });
      $$('[data-model-next]', slider).forEach(function (b) { b.addEventListener('click', function () { go(index + 1); }); });

      /* swipe */
      var startX = null;
      slider.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
      slider.addEventListener('touchend', function (e) {
        if (startX === null) return;
        var dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 60) go(index + (dx < 0 ? 1 : -1));
        startX = null;
      }, { passive: true });

      go(0); booted = true;
    });
  }

  /* ------------------------------------------------------------------
     Accordions (FAQ etc.)
     ------------------------------------------------------------------ */
  function initAccordions() {
    $$('[data-accordion]').forEach(function (root) {
      var single = root.getAttribute('data-accordion') === 'single';
      $$('.accordion__trigger', root).forEach(function (trigger) {
        var panel = document.getElementById(trigger.getAttribute('aria-controls'));
        if (!panel) return;
        trigger.addEventListener('click', function () {
          var isOpen = trigger.getAttribute('aria-expanded') === 'true';
          if (single) {
            $$('.accordion__trigger', root).forEach(function (t) {
              t.setAttribute('aria-expanded', 'false');
              var p = document.getElementById(t.getAttribute('aria-controls'));
              if (p) p.style.maxHeight = '0px';
            });
          }
          trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
          panel.style.maxHeight = isOpen ? '0px' : panel.scrollHeight + 'px';
        });
      });
    });
  }

  /* ------------------------------------------------------------------
     Tech hotspots — link numbered markers to their feature rows
     ------------------------------------------------------------------ */
  function initTechHotspots() {
    $$('[data-tech]').forEach(function (root) {
      var spots = $$('.tech__hotspot', root);
      var features = $$('.tech-feature', root);
      function highlight(id) {
        features.forEach(function (f) {
          f.classList.toggle('is-dimmed', id !== null && f.getAttribute('data-feature') !== id);
        });
        spots.forEach(function (s) {
          s.setAttribute('aria-expanded', s.getAttribute('data-feature') === id ? 'true' : 'false');
        });
      }
      spots.forEach(function (spot) {
        var id = spot.getAttribute('data-feature');
        spot.addEventListener('mouseenter', function () { highlight(id); });
        spot.addEventListener('focus', function () { highlight(id); });
        spot.addEventListener('mouseleave', function () { highlight(null); });
        spot.addEventListener('blur', function () { highlight(null); });
        spot.addEventListener('click', function () {
          var target = root.querySelector('.tech-feature[data-feature="' + id + '"]');
          if (target) target.scrollIntoView({ behavior: motionOff ? 'auto' : 'smooth', block: 'center' });
        });
      });
    });
  }

  /* ------------------------------------------------------------------
     Sticky mobile CTA bars
     ------------------------------------------------------------------ */
  function initStickyCtas() {
    var bar = $('[data-sticky-cta]');
    if (!bar) return;
    var trigger = $('[data-sticky-cta-watch]') || $('.product-info__title');
    if (!trigger) { bar.classList.add('is-visible'); return; }
    if (!('IntersectionObserver' in window)) { bar.classList.add('is-visible'); return; }
    var io = new IntersectionObserver(function (entries) {
      bar.classList.toggle('is-visible', !entries[0].isIntersecting);
    }, { threshold: 0 });
    io.observe(trigger);
  }

  /* ------------------------------------------------------------------
     Page transition veil (internal navigation)
     ------------------------------------------------------------------ */
  function initPageTransitions() {
    if (motionOff) return;
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href]');
      if (!link) return;
      if (link.target === '_blank' || link.hasAttribute('download') || e.metaKey || e.ctrlKey || e.shiftKey) return;
      var url = new URL(link.href, window.location.origin);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.hash) return;
      if (link.closest('[data-no-transition]')) return;
      e.preventDefault();
      document.body.classList.add('is-leaving');
      setTimeout(function () { window.location.href = link.href; }, 380);
    });
    window.addEventListener('pageshow', function () { document.body.classList.remove('is-leaving'); });
  }

  /* ------------------------------------------------------------------
     Newsletter inline feedback
     ------------------------------------------------------------------ */
  function initNewsletterForms() {
    var params = new URLSearchParams(window.location.search);
    if (params.get('customer_posted') === 'true') {
      $$('[data-newsletter-success]').forEach(function (el) { el.hidden = false; });
      var target = $('[data-newsletter-success]');
      if (target) target.closest('.shopify-section').scrollIntoView();
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    initReveals();
    initParallax();
    initHeader();
    initHeroes();
    initModelSliders();
    initAccordions();
    initTechHotspots();
    initStickyCtas();
    initPageTransitions();
    initNewsletterForms();
  });
})();
